import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';

interface TenantItem {
  id: string;
  name: string;
  description?: string;
  domainNames?: string[] | string;
  licenseKey?: string;
  settings?: any;
  createdDate?: string;
  createdBy?: string;
  lastUpdatedDate?: string;
  lastUpdatedBy?: string;
  active?: boolean;
}

const TenantManagement: React.FC = () => {
  const theme = useTheme();
  const { getAllTenants } = useApiContext();
  const { user, switchTenant } = useUserContext();

  const [tenants, setTenants] = useState<TenantItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenantDetails, setSelectedTenantDetails] = useState<TenantItem | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);

  const fetchTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllTenants();
      setTenants(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to load tenants', err);
      setError(err?.message || 'Failed to fetch platform tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCopyKey = (key: string, id: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(id);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const handleSwitchTenant = (tenantId: string) => {
    if (switchTenant) {
      switchTenant(tenantId);
    }
  };

  const parseDomains = (domains: any): string[] => {
    if (!domains) return [];
    if (Array.isArray(domains)) return domains;
    if (typeof domains === 'string') {
      try {
        const parsed = JSON.parse(domains);
        return Array.isArray(parsed) ? parsed : [domains];
      } catch {
        return [domains];
      }
    }
    return [];
  };

  const filteredTenants = tenants.filter((t) => {
    const term = searchTerm.toLowerCase();
    const nameMatch = (t.name || '').toLowerCase().includes(term);
    const idMatch = (t.id || '').toLowerCase().includes(term);
    const descMatch = (t.description || '').toLowerCase().includes(term);
    const domainMatch = parseDomains(t.domainNames).some((d) => d.toLowerCase().includes(term));
    return nameMatch || idMatch || descMatch || domainMatch;
  });

  const totalDomainsCount = new Set(
    tenants.flatMap((t) => parseDomains(t.domainNames))
  ).size;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, height: '100%', overflowY: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <BusinessIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Tenant Management
            </Typography>
            <Chip
              label="Super Admin"
              size="small"
              icon={<AdminPanelSettingsIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.12),
                color: theme.palette.secondary.main,
                fontWeight: 700,
                fontSize: '11px',
                border: '1px solid',
                borderColor: alpha(theme.palette.secondary.main, 0.25),
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View and manage all tenant organizations across the Minotii platform.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchTenants}
          disabled={loading}
          sx={{ textTransform: 'none', borderRadius: '10px' }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <Card
          variant="outlined"
          sx={{
            borderRadius: '14px',
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(8px)',
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={650}>
              TOTAL TENANTS
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
              {tenants.length}
            </Typography>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            borderRadius: '14px',
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(8px)',
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={650}>
              TOTAL REGISTERED DOMAINS
            </Typography>
            <Typography variant="h4" fontWeight={800} color="secondary.main" sx={{ mt: 0.5 }}>
              {totalDomainsCount}
            </Typography>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            borderRadius: '14px',
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(8px)',
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={650}>
              CURRENT ACTIVE CONTEXT
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="primary.main"
              noWrap
              sx={{ mt: 0.5 }}
            >
              {user?.tenantId || 'Default'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 2.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search tenants by name, ID, domain..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 480,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
            },
          }}
        />
      </Box>

      {/* Error state */}
      {error && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            borderColor: 'error.main',
            bgcolor: alpha(theme.palette.error.main, 0.08),
            borderRadius: '12px',
          }}
        >
          <Typography color="error.main" variant="body2" fontWeight={600}>
            {error}
          </Typography>
        </Paper>
      )}

      {/* Tenants Table */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: '14px',
          overflow: 'hidden',
          borderColor: 'divider',
        }}
      >
        <TableContainer>
          <Table size="medium">
            <TableHead sx={{ bgcolor: alpha(theme.palette.action.hover, 0.06) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase' }}>
                  Tenant Name / ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase' }}>
                  Domains
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase' }}>
                  License Key
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase' }}>
                  Created
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Loading platform tenants...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <BusinessIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                      No tenants found
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {searchTerm ? 'No tenants match your search filter' : 'No tenants exist in the database'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map((t) => {
                  const isCurrent = user?.tenantId === t.id;
                  const domains = parseDomains(t.domainNames);
                  const shortKey = t.licenseKey
                    ? `${t.licenseKey.slice(0, 10)}...${t.licenseKey.slice(-6)}`
                    : 'None';

                  return (
                    <TableRow
                      key={t.id}
                      hover
                      sx={{
                        bgcolor: isCurrent ? alpha(theme.palette.primary.main, 0.04) : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2" fontWeight={750}>
                              {t.name || t.id}
                            </Typography>
                            {isCurrent && (
                              <Chip
                                label="Active"
                                size="small"
                                color="primary"
                                icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                                sx={{ height: 20, fontSize: '10px', fontWeight: 700 }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            ID: {t.id}
                          </Typography>
                          {t.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {t.description}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {domains.length > 0 ? (
                            domains.map((d) => (
                              <Chip
                                key={d}
                                label={d}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '11px', height: 22 }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {t.licenseKey ? (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <KeyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" fontFamily="monospace">
                              {shortKey}
                            </Typography>
                            <Tooltip title={copiedKeyId === t.id ? 'Copied!' : 'Copy Key'}>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyKey(t.licenseKey!, t.id)}
                                sx={{ p: 0.5 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            No License
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {t.createdDate ? new Date(t.createdDate).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setSelectedTenantDetails(t)}
                            >
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>

                          {!isCurrent && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
                              onClick={() => handleSwitchTenant(t.id)}
                              sx={{
                                textTransform: 'none',
                                fontSize: '11px',
                                py: 0.25,
                                px: 1.25,
                                borderRadius: '8px',
                              }}
                            >
                              Switch
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Details Dialog */}
      {selectedTenantDetails && (
        <Dialog
          open={Boolean(selectedTenantDetails)}
          onClose={() => setSelectedTenantDetails(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Tenant Details: {selectedTenantDetails.name || selectedTenantDetails.id}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={650}>
                  TENANT ID
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {selectedTenantDetails.id}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={650}>
                  NAME
                </Typography>
                <Typography variant="body2">
                  {selectedTenantDetails.name}
                </Typography>
              </Box>

              {selectedTenantDetails.description && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={650}>
                    DESCRIPTION
                  </Typography>
                  <Typography variant="body2">
                    {selectedTenantDetails.description}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={650}>
                  DOMAINS
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {parseDomains(selectedTenantDetails.domainNames).map((d) => (
                    <Chip key={d} label={d} size="small" />
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={650}>
                  FULL LICENSE KEY
                </Typography>
                <Typography
                  variant="caption"
                  fontFamily="monospace"
                  sx={{
                    display: 'block',
                    wordBreak: 'break-all',
                    bgcolor: alpha(theme.palette.action.hover, 0.08),
                    p: 1,
                    borderRadius: '8px',
                    mt: 0.5,
                  }}
                >
                  {selectedTenantDetails.licenseKey || 'None'}
                </Typography>
              </Box>

              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created: {selectedTenantDetails.createdDate ? new Date(selectedTenantDetails.createdDate).toLocaleString() : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created By: {selectedTenantDetails.createdBy || 'System'}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            {user?.tenantId !== selectedTenantDetails.id && (
              <Button
                variant="contained"
                startIcon={<SwapHorizIcon />}
                onClick={() => {
                  handleSwitchTenant(selectedTenantDetails.id);
                  setSelectedTenantDetails(null);
                }}
                sx={{ textTransform: 'none' }}
              >
                Switch To This Tenant
              </Button>
            )}
            <Button onClick={() => setSelectedTenantDetails(null)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default TenantManagement;
